import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useProject } from '@/context/ProjectContext';
import { useAuth } from '@/context/AuthContext';
import { questionsAPI, publicLandingAPI, uploadAPI, questionLibraryAPI, projectMappingCategoriesAPI, mappingCategoriesLibraryAPI } from '@/lib/api';
import LandingPreview from '@/components/LandingPreview';
import AddQuestionWizard from '@/components/AddQuestionWizard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Plus, 
  MoreVertical, 
  Pencil, 
  Trash2, 
  FileQuestion,
  GripVertical,
  X,
  Info,
  Globe,
  User,
  UsersRound,
  FileText,
  HelpCircle,
  Users,
  UserCheck,
  Shield,
  Save,
  Copy,
  Check,
  Link as LinkIcon,
  QrCode,
  Download,
  Upload,
  Image,
  ExternalLink,
  Loader2,
  Filter,
  BookOpen,
  RefreshCw,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';

const questionTypes = [
  { value: 'free_text', label: 'Free Text' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'slider', label: 'Slider' },
  { value: 'yes_no', label: 'Yes/No' },
  { value: 'checkbox', label: 'Checkbox (Multiple)' },
  { value: 'multi_topic', label: 'Multi-Topic (Ranked List)' },
];

const questionContexts = [
  { value: 'public_landing', label: 'Public Landing', icon: Globe, description: 'Configure the public-facing landing page' },
  { value: 'about_me', label: 'About Me', icon: User, description: 'Questions about the stakeholder themselves' },
  { value: 'about_others', label: 'About Others', icon: UsersRound, description: 'Questions about relationships with others' },
  { value: 'mapping', label: 'Mapping', icon: LinkIcon, description: 'Configure relationship mapping categories' },
];

const publicLandingTabs = [
  { value: 'landing_page', label: 'Landing Page', icon: FileText, description: 'Welcome content and messaging' },
  { value: 'self_segmentation', label: 'Self-Segmentation', icon: Users, description: 'Let users select their segments' },
  { value: 'public_questions', label: 'Public Questions', icon: HelpCircle, description: 'Questions for unverified users' },
  { value: 'self_identification', label: 'Self-Identification', icon: UserCheck, description: 'Optional user identification' },
];

const defaultSliderConfig = {
  left_label: 'Low',
  middle_label: 'Medium',
  right_label: 'High',
  min_value: 0,
  max_value: 10,
  left_color: null,    // System teal by default
  middle_color: null,  // System teal by default
  right_color: null,   // System teal by default
};

// Custom gradient colors when user enables them
const customGradientColors = {
  left_color: '#ef4444',    // Red
  middle_color: '#eab308',  // Yellow
  right_color: '#22c55e',   // Green
};

export default function QuestionsPage() {
  const { projectId } = useParams();
  const { token } = useAuth();
  const { currentProject, drivers, segments, segmentTypes, selectProject } = useProject();
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [allAboutMeQuestions, setAllAboutMeQuestions] = useState([]); // Store all questions for driver counts
  const [sheetOpen, setSheetOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState('all');
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [activeContext, setActiveContext] = useState('about_me');
  const [searchParams] = useSearchParams();

  // Set initial tab from URL param
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['about_me', 'about_others', 'public_landing', 'mapping'].includes(tab)) {
      setActiveContext(tab);
    }
  }, [searchParams]);
  const [activePublicTab, setActivePublicTab] = useState('landing_page');
  const [submitting, setSubmitting] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  
  // Public Landing Config state
  const [landingConfig, setLandingConfig] = useState({
    welcome_title: '',
    welcome_message: '',
    anonymity_message: '',
    logo_url: '',
    hero_image_url: '',
    learn_more_url: '',
    enable_recaptcha: true,
    allow_self_segmentation: true,
    allowed_segment_type_ids: [],
    allow_self_identification: true,
    self_id_prompt: '',
    involvement_question: '',
  });
  
  const [formData, setFormData] = useState({
    text: '',
    heading: '',
    question_type: 'free_text',
    question_context: 'about_me',
    driver_id: '',
    segment_ids: [],
    options: [''],
    slider_config: defaultSliderConfig,
    required: true,
    allow_skip: false,
  });

  // Question Library state
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryQuestions, setLibraryQuestions] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [filterByDrivers, setFilterByDrivers] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteAction, setDeleteAction] = useState('delete'); // 'delete' or 'hide'

  // Edit warning state
  const [editWarningOpen, setEditWarningOpen] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null);

  // Mapping Categories state (for About Others)
  const [mappingCategories, setMappingCategories] = useState([]);
  const [mappingCategoriesLibrary, setMappingCategoriesLibrary] = useState([]);
  const [mappingCategoryDialogOpen, setMappingCategoryDialogOpen] = useState(false);
  const [editingMappingCategory, setEditingMappingCategory] = useState(null);
  const [mappingCategoryForm, setMappingCategoryForm] = useState({ name: '', description: '', icon: '' });
  const [importMappingDialogOpen, setImportMappingDialogOpen] = useState(false);
  const [selectedImportCategories, setSelectedImportCategories] = useState([]);

  // Response stats state (for About Me questions)
  const [responseStats, setResponseStats] = useState({});
  const [loadingStats, setLoadingStats] = useState(false);
  
  // Active driver for About Me horizontal tabs
  const [activeDriverId, setActiveDriverId] = useState(null);
  const driverInitRef = useRef(false);

  const fetchLibraryQuestions = useCallback(async (context) => {
    setLibraryLoading(true);
    try {
      const response = context === 'about_me' 
        ? await questionLibraryAPI.getAboutMe()
        : await questionLibraryAPI.getAboutOthers();
      setLibraryQuestions(response.data.questions || []);
    } catch (err) {
      console.error('Failed to fetch library questions:', err);
      setLibraryQuestions([]);
    } finally {
      setLibraryLoading(false);
    }
  }, []);

  const openLibrary = () => {
    fetchLibraryQuestions(formData.question_context);
    setLibraryOpen(true);
  };

  const importFromLibrary = async (libraryQuestion) => {
    // Create the question from library template
    const payload = {
      text: libraryQuestion.text,
      heading: libraryQuestion.heading || null,
      question_type: libraryQuestion.question_type,
      question_context: formData.question_context,
      driver_id: formData.question_context === 'about_me' ? formData.driver_id : null,
      segment_ids: [],
      required: libraryQuestion.required || true,
      allow_skip: false,
      options: libraryQuestion.options || [],
      slider_config: libraryQuestion.slider_config || null,
    };

    try {
      await questionsAPI.create(projectId, payload);
      toast.success('Question imported from library');
      await fetchQuestions();
      setLibraryOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import question');
    }
  };

  const fetchQuestions = useCallback(async () => {
    try {
      const contextToFetch = activeContext === 'public_landing' ? 'public_landing' : activeContext;
      const response = await questionsAPI.list(projectId, null, contextToFetch);
      let filteredQuestions = response.data;
      
      if (activeContext === 'about_me') {
        // Store all questions for driver counts (before filtering)
        setAllAboutMeQuestions(response.data);
        
        if (selectedDriver !== 'all') {
          filteredQuestions = filteredQuestions.filter(q => q.driver_id === selectedDriver);
        }
        if (selectedSegment !== 'all') {
          filteredQuestions = filteredQuestions.filter(q => 
            q.segment_ids.length === 0 || q.segment_ids.includes(selectedSegment)
          );
        }
      }
      
      setQuestions(filteredQuestions);
    } catch (err) {
      console.error('Failed to fetch questions:', err);
    }
  }, [projectId, activeContext, selectedDriver, selectedSegment]);

  // Mapping Categories functions
  const fetchMappingCategories = useCallback(async () => {
    try {
      const response = await projectMappingCategoriesAPI.list(projectId);
      setMappingCategories(response.data?.categories || []);
    } catch (err) {
      console.error('Failed to fetch mapping categories:', err);
    }
  }, [projectId]);

  // Fetch response stats for questions
  const fetchResponseStats = useCallback(async () => {
    if (activeContext !== 'about_me') return;
    setLoadingStats(true);
    try {
      const response = await questionsAPI.getResponseStats(projectId);
      setResponseStats(response.data || {});
    } catch (err) {
      console.error('Failed to fetch response stats:', err);
    } finally {
      setLoadingStats(false);
    }
  }, [projectId, activeContext]);

  const fetchMappingCategoriesLibrary = useCallback(async () => {
    try {
      const response = await mappingCategoriesLibraryAPI.getForUsers();
      setMappingCategoriesLibrary(response.data?.categories || []);
    } catch (err) {
      console.error('Failed to fetch mapping categories library:', err);
    }
  }, []);

  const handleSaveMappingCategory = async () => {
    if (!mappingCategoryForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    try {
      if (editingMappingCategory) {
        await projectMappingCategoriesAPI.update(projectId, editingMappingCategory.id, mappingCategoryForm);
        toast.success('Mapping category updated');
      } else {
        await projectMappingCategoriesAPI.create(projectId, mappingCategoryForm);
        toast.success('Mapping category created');
      }
      setMappingCategoryDialogOpen(false);
      setEditingMappingCategory(null);
      setMappingCategoryForm({ name: '', description: '', icon: '' });
      fetchMappingCategories();
    } catch (err) {
      toast.error('Failed to save mapping category');
    }
  };

  const handleDeleteMappingCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this mapping category?')) return;
    
    try {
      await projectMappingCategoriesAPI.delete(projectId, categoryId);
      toast.success('Mapping category deleted');
      fetchMappingCategories();
    } catch (err) {
      toast.error('Failed to delete mapping category');
    }
  };

  const handleImportMappingCategories = async () => {
    if (selectedImportCategories.length === 0) {
      toast.error('Please select at least one category');
      return;
    }
    try {
      await projectMappingCategoriesAPI.importFromLibrary(projectId, selectedImportCategories);
      toast.success(`${selectedImportCategories.length} mapping categories imported`);
      setImportMappingDialogOpen(false);
      setSelectedImportCategories([]);
      fetchMappingCategories();
    } catch (err) {
      toast.error('Failed to import mapping categories');
    }
  };

  const handleMoveMappingCategory = async (categoryIndex, direction) => {
    const categories = [...mappingCategories];
    const newIndex = categoryIndex + direction;
    
    if (newIndex < 0 || newIndex >= categories.length) return;
    
    [categories[categoryIndex], categories[newIndex]] = [categories[newIndex], categories[categoryIndex]];
    setMappingCategories(categories);
    
    // Save the new order to backend (need to add endpoint)
    try {
      const categoryIds = categories.map(c => c.id);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/mapping-categories/reorder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ category_ids: categoryIds })
      });
      if (!response.ok) throw new Error('Failed to reorder');
    } catch (error) {
      toast.error('Failed to save order');
      fetchMappingCategories(); // Revert on error
    }
  };

  const fetchLandingConfig = useCallback(async () => {
    try {
      const response = await publicLandingAPI.getConfig(projectId);
      setLandingConfig(response.data);
    } catch (err) {
      console.error('Failed to fetch landing config:', err);
    }
  }, [projectId]);

  useEffect(() => {
    const load = async () => {
      if (!currentProject || currentProject.id !== projectId) {
        await selectProject(projectId);
      }
      await Promise.all([fetchQuestions(), fetchLandingConfig()]);
      setLoading(false);
    };
    load();
  }, [projectId, currentProject, selectProject, fetchQuestions, fetchLandingConfig]);

  useEffect(() => {
    if (!loading) {
      fetchQuestions();
    }
  }, [activeContext, selectedDriver, selectedSegment, loading, fetchQuestions]);

  // Fetch mapping categories when About Others or Mapping is selected
  useEffect(() => {
    if ((activeContext === 'about_others' || activeContext === 'mapping') && projectId) {
      fetchMappingCategories();
    }
  }, [activeContext, projectId, fetchMappingCategories]);

  // Fetch response stats when About Me is selected
  useEffect(() => {
    if (activeContext === 'about_me' && projectId) {
      fetchResponseStats();
    }
  }, [activeContext, projectId, fetchResponseStats]);

  // Set first driver as active when entering About Me context (only on initial entry)
  useEffect(() => {
    if (activeContext !== 'about_me') {
      driverInitRef.current = false;
      return;
    }
    if (activeContext === 'about_me' && drivers.length > 0 && !driverInitRef.current) {
      driverInitRef.current = true;
      setActiveDriverId(drivers[0].id);
      setSelectedDriver(drivers[0].id); // Also set selectedDriver to filter questions
    }
  }, [activeContext, drivers]);

  const saveLandingConfig = async () => {
    setSavingConfig(true);
    try {
      await publicLandingAPI.updateConfig(projectId, landingConfig);
      toast.success('Configuration saved');
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      const response = await uploadAPI.upload(projectId, file, 'logo');
      const newConfig = { ...landingConfig, logo_url: response.data.url };
      setLandingConfig(newConfig);
      // Auto-save after upload
      await publicLandingAPI.updateConfig(projectId, newConfig);
      toast.success('Logo uploaded and saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingHero(true);
    try {
      const response = await uploadAPI.upload(projectId, file, 'hero');
      const newConfig = { ...landingConfig, hero_image_url: response.data.url };
      setLandingConfig(newConfig);
      // Auto-save after upload
      await publicLandingAPI.updateConfig(projectId, newConfig);
      toast.success('Hero image uploaded and saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingHero(false);
    }
  };

  const resetForm = () => {
    setFormData({
      text: '',
      heading: '',
      question_type: 'free_text',
      question_context: activeContext,
      driver_id: activeContext === 'about_me' ? (drivers[0]?.id || '') : '',
      segment_ids: [],
      options: [''],
      slider_config: defaultSliderConfig,
      required: true,
      allow_skip: false,
    });
    setEditingQuestion(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.heading?.trim()) {
      toast.error('Please enter a heading for the question');
      return;
    }
    
    if (formData.question_context === 'about_me' && !formData.driver_id) {
      toast.error('Please select a driver for About Me questions');
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        text: formData.text,
        heading: formData.heading,
        question_type: formData.question_type,
        question_context: formData.question_context,
        driver_id: formData.question_context === 'about_me' ? formData.driver_id : null,
        segment_ids: formData.segment_ids,
        required: formData.required,
        allow_skip: formData.allow_skip,
      };

      if (['multiple_choice', 'dropdown', 'checkbox'].includes(formData.question_type)) {
        payload.options = formData.options.filter(o => o.trim());
      }

      if (formData.question_type === 'slider') {
        payload.slider_config = formData.slider_config;
      }

      if (editingQuestion) {
        await questionsAPI.update(projectId, editingQuestion.id, payload);
        toast.success('Question updated');
      } else {
        await questionsAPI.create(projectId, payload);
        toast.success('Question created');
      }

      await fetchQuestions();
      setSheetOpen(false);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save question');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setFormData({
      text: question.text,
      heading: question.heading || '',
      question_type: question.question_type,
      question_context: question.question_context || 'about_me',
      driver_id: question.driver_id || '',
      segment_ids: question.segment_ids || [],
      options: question.options || [''],
      slider_config: question.slider_config || defaultSliderConfig,
      required: question.required,
      allow_skip: question.allow_skip || false,
    });
    // Show edit warning if question may have responses
    setPendingEdit(question);
    setEditWarningOpen(true);
  };

  const confirmEdit = () => {
    setEditWarningOpen(false);
    setSheetOpen(true);
    setPendingEdit(null);
  };

  const handleDelete = (question) => {
    setDeleteTarget(question);
    setDeleteAction('delete');
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      if (deleteAction === 'hide') {
        // Hide the question (set hidden flag)
        await questionsAPI.update(projectId, deleteTarget.id, { hidden: true });
        toast.success('Question hidden - responses preserved');
      } else {
        // Delete the question
        await questionsAPI.delete(projectId, deleteTarget.id);
        toast.success('Question and responses deleted');
      }
      await fetchQuestions();
    } catch (err) {
      toast.error(`Failed to ${deleteAction} question`);
    } finally {
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  const openCreateSheet = () => {
    resetForm();
    setWizardOpen(true);
  };

  const addOption = () => {
    setFormData(prev => ({ ...prev, options: [...prev.options, ''] }));
  };

  const updateOption = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const removeOption = (index) => {
    if (formData.options.length <= 1) return;
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const toggleSegment = (segmentId) => {
    setFormData(prev => ({
      ...prev,
      segment_ids: prev.segment_ids.includes(segmentId)
        ? prev.segment_ids.filter(id => id !== segmentId)
        : [...prev.segment_ids, segmentId]
    }));
  };

  const toggleSegmentType = (segmentTypeId) => {
    setLandingConfig(prev => ({
      ...prev,
      allowed_segment_type_ids: prev.allowed_segment_type_ids.includes(segmentTypeId)
        ? prev.allowed_segment_type_ids.filter(id => id !== segmentTypeId)
        : [...prev.allowed_segment_type_ids, segmentTypeId]
    }));
  };

  const getDriverName = (driverId) => {
    return drivers.find(d => d.id === driverId)?.name || 'Unknown';
  };

  const getSegmentNames = (segmentIds) => {
    if (!segmentIds || segmentIds.length === 0) return 'All segments';
    return segmentIds.map(id => segments.find(s => s.id === id)?.name || 'Unknown').join(', ');
  };

  const getTypeLabel = (type) => {
    return questionTypes.find(t => t.value === type)?.label || type;
  };

  const activeContextInfo = questionContexts.find(c => c.value === activeContext);

  // Render Public Landing Content
  const renderPublicLandingContent = () => {
    return (
      <div className="space-y-6">
        {/* Public Landing Tabs */}
        <Tabs value={activePublicTab} onValueChange={setActivePublicTab} className="w-full">
          <TabsList className="grid grid-cols-4 w-full">
            {publicLandingTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <TabsTrigger key={tab.value} value={tab.value} className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Landing Page CMS */}
          <TabsContent value="landing_page" className="space-y-6 mt-6">
            {/* Public Link & QR Code */}
            <PublicLinkCard projectId={projectId} />

            {/* Live Preview */}
            <LandingPreview
              config={landingConfig}
              questions={questions}
              segmentTypes={(segmentTypes || []).filter(st => st.id !== landingConfig.involvement_segment_type_id)}
              involvementType={landingConfig.involvement_segment_type_id ? {
                ...(segmentTypes || []).find(st => st.id === landingConfig.involvement_segment_type_id),
                segments: (segments || []).filter(s => s.segment_type_id === landingConfig.involvement_segment_type_id)
              } : null}
              projectName={currentProject?.name}
            />

            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#5AA2AE]" />
                  Welcome Content
                </CardTitle>
                <CardDescription>
                  Customize the welcome message for visitors to your project's public landing page
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="welcome_title">Welcome Title</Label>
                  <Input
                    id="welcome_title"
                    value={landingConfig.welcome_title}
                    onChange={(e) => setLandingConfig(prev => ({ ...prev, welcome_title: e.target.value }))}
                    placeholder="Welcome to our project"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="welcome_message">Welcome Message</Label>
                  <Textarea
                    id="welcome_message"
                    value={landingConfig.welcome_message}
                    onChange={(e) => setLandingConfig(prev => ({ ...prev, welcome_message: e.target.value }))}
                    placeholder="We value your input and would like to hear your perspective..."
                    rows={4}
                  />
                </div>

                <Separator />

                {/* Logo Upload */}
                <div className="space-y-2">
                  <Label>Logo (Optional)</Label>
                  <div className="flex items-center gap-4">
                    {landingConfig.logo_url ? (
                      <div className="relative group">
                        <img 
                          src={landingConfig.logo_url} 
                          alt="Logo" 
                          className="h-16 w-auto object-contain rounded border"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute -top-2 -right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={async () => {
                            const newConfig = { ...landingConfig, logo_url: '' };
                            setLandingConfig(newConfig);
                            try {
                              await publicLandingAPI.updateConfig(projectId, newConfig);
                              toast.success('Logo removed');
                            } catch (err) {
                              toast.error('Failed to save');
                            }
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="h-16 w-24 border-2 border-dashed rounded flex items-center justify-center text-slate-400">
                        <Image className="h-6 w-6" />
                      </div>
                    )}
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        id="logo-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('logo-upload').click()}
                        disabled={uploadingLogo}
                      >
                        {uploadingLogo ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4 mr-2" />
                        )}
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                      <p className="text-xs text-slate-500 mt-1">Max 5MB. PNG, JPG, GIF, WebP</p>
                    </div>
                  </div>
                </div>

                {/* Hero Image Upload */}
                <div className="space-y-2">
                  <Label>Hero Image (Optional)</Label>
                  <div className="space-y-2">
                    {landingConfig.hero_image_url ? (
                      <div className="relative group">
                        <img 
                          src={landingConfig.hero_image_url} 
                          alt="Hero" 
                          className="w-full h-40 object-cover rounded border"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={async () => {
                            const newConfig = { ...landingConfig, hero_image_url: '' };
                            setLandingConfig(newConfig);
                            try {
                              await publicLandingAPI.updateConfig(projectId, newConfig);
                              toast.success('Hero image removed');
                            } catch (err) {
                              toast.error('Failed to save');
                            }
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="h-32 border-2 border-dashed rounded flex items-center justify-center text-slate-400">
                        <div className="text-center">
                          <Image className="h-8 w-8 mx-auto mb-2" />
                          <span className="text-sm">No hero image</span>
                        </div>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleHeroUpload}
                      className="hidden"
                      id="hero-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('hero-upload').click()}
                      disabled={uploadingHero}
                    >
                      {uploadingHero ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      {uploadingHero ? 'Uploading...' : 'Upload Hero Image'}
                    </Button>
                  </div>
                </div>

                <Separator />

                {/* Learn More URL */}
                <div className="space-y-2">
                  <Label htmlFor="learn_more_url">Learn More Link (Optional)</Label>
                  <Input
                    id="learn_more_url"
                    type="url"
                    value={landingConfig.learn_more_url || ''}
                    onChange={(e) => setLandingConfig(prev => ({ ...prev, learn_more_url: e.target.value }))}
                    onBlur={(e) => {
                      // Auto-add https:// if user enters a URL without protocol
                      const url = e.target.value.trim();
                      if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
                        setLandingConfig(prev => ({ ...prev, learn_more_url: `https://${url}` }));
                      }
                    }}
                    placeholder="https://example.com/about-project"
                  />
                  <p className="text-xs text-slate-500">
                    External link to more information about the project. Must include https://
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-[#5AA2AE]" />
                  Privacy & Security
                </CardTitle>
                <CardDescription>
                  Configure anonymity messaging and bot protection
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="anonymity_message">Anonymity Message</Label>
                  <Textarea
                    id="anonymity_message"
                    value={landingConfig.anonymity_message}
                    onChange={(e) => setLandingConfig(prev => ({ ...prev, anonymity_message: e.target.value }))}
                    placeholder="Your responses are anonymous..."
                    rows={3}
                  />
                  <p className="text-xs text-slate-500">Reassure users that their feedback is protected</p>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="enable_recaptcha">Enable reCAPTCHA</Label>
                    <p className="text-xs text-slate-500">Verify users are human before allowing them to continue</p>
                  </div>
                  <Switch
                    id="enable_recaptcha"
                    checked={landingConfig.enable_recaptcha}
                    onCheckedChange={(checked) => setLandingConfig(prev => ({ ...prev, enable_recaptcha: checked }))}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button 
                onClick={saveLandingConfig} 
                disabled={savingConfig}
                className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              >
                <Save className="mr-2 h-4 w-4" />
                {savingConfig ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </TabsContent>

          {/* Public Questions */}
          <TabsContent value="public_questions" className="space-y-6 mt-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">Public Questions</h3>
                <p className="text-sm text-slate-500">Questions for public users. Target specific segments to show questions based on self-segmentation.</p>
              </div>
              <Button 
                className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                onClick={() => {
                  setFormData(prev => ({ ...prev, question_context: 'public_landing' }));
                  openCreateSheet();
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Question
              </Button>
            </div>
            
            {questions.length === 0 ? (
              <Card className="dashboard-card">
                <CardContent className="py-16 text-center">
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                    <HelpCircle className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No public questions yet</h3>
                  <p className="text-slate-500 mb-4">Add questions that public users can answer. Optionally target specific segments.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {questions.map((question, index) => (
                  <QuestionCard 
                    key={question.id}
                    question={question}
                    index={index}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    getTypeLabel={getTypeLabel}
                    getDriverName={getDriverName}
                    getSegmentNames={getSegmentNames}
                    responseStats={responseStats}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Self-Segmentation */}
          <TabsContent value="self_segmentation" className="space-y-6 mt-6">
            {/* Involvement Question Configuration */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#5AA2AE]" />
                  Involvement Question
                </CardTitle>
                <CardDescription>
                  Configure the primary involvement question shown as a standalone card selection
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="involvement_question">Question Text</Label>
                  <Input
                    id="involvement_question"
                    value={landingConfig.involvement_question || ''}
                    onChange={(e) => setLandingConfig(prev => ({ ...prev, involvement_question: e.target.value }))}
                    placeholder="How would you describe your involvement with the project?"
                  />
                  <p className="text-xs text-slate-500">
                    Default: "How would you describe your involvement with the project?"
                  </p>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Involvement Segment Type</Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Select which segment type represents "Involvement". This will be shown as a card-based selection with icons and descriptions.
                  </p>
                  <Select 
                    value={landingConfig.involvement_segment_type_id || ''} 
                    onValueChange={(v) => setLandingConfig(prev => ({ ...prev, involvement_segment_type_id: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select involvement segment type" />
                    </SelectTrigger>
                    <SelectContent>
                      {(segmentTypes || []).map(type => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Involvement Required</Label>
                    <p className="text-xs text-slate-500">Users must select an involvement level to proceed</p>
                  </div>
                  <Switch
                    checked={landingConfig.involvement_required !== false}
                    onCheckedChange={(checked) => setLandingConfig(prev => ({ ...prev, involvement_required: checked }))}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Segmentation Flow Conditions */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-[#5AA2AE]" />
                  Segmentation Flow Conditions
                </CardTitle>
                <CardDescription>
                  Configure which involvement levels require full segmentation, and which can skip to identification
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {landingConfig.involvement_segment_type_id ? (
                  <>
                    <div className="space-y-2">
                      <Label>Require Full Segmentation For</Label>
                      <p className="text-xs text-slate-500 mb-2">
                        Select which involvement levels should see the full segmentation questions. 
                        Users with other involvement levels will skip directly to self-identification.
                      </p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {segments
                          .filter(seg => {
                            const segType = (segmentTypes || []).find(st => st.id === landingConfig.involvement_segment_type_id);
                            return segType && segments.some(s => s.id === seg.id && s.segment_type_id === segType.id);
                          })
                          .length === 0 ? (
                            // If no segments found in the filter, show all segments for the selected type
                            segments
                              .filter(seg => seg.segment_type_id === landingConfig.involvement_segment_type_id)
                              .map(seg => (
                                <div key={seg.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 border">
                                  <Checkbox
                                    id={`cond-${seg.id}`}
                                    checked={(landingConfig.segmentation_conditions || []).includes(seg.id)}
                                    onCheckedChange={() => {
                                      const conditions = landingConfig.segmentation_conditions || [];
                                      setLandingConfig(prev => ({
                                        ...prev,
                                        segmentation_conditions: conditions.includes(seg.id)
                                          ? conditions.filter(id => id !== seg.id)
                                          : [...conditions, seg.id]
                                      }));
                                    }}
                                  />
                                  <Label 
                                    htmlFor={`cond-${seg.id}`}
                                    className="text-sm font-normal cursor-pointer flex-1"
                                  >
                                    {seg.name}
                                    {seg.description && (
                                      <span className="text-xs text-slate-400 block">{seg.description}</span>
                                    )}
                                  </Label>
                                </div>
                              ))
                          ) : (
                            segments
                              .filter(seg => seg.segment_type_id === landingConfig.involvement_segment_type_id)
                              .map(seg => (
                                <div key={seg.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 border">
                                  <Checkbox
                                    id={`cond-${seg.id}`}
                                    checked={(landingConfig.segmentation_conditions || []).includes(seg.id)}
                                    onCheckedChange={() => {
                                      const conditions = landingConfig.segmentation_conditions || [];
                                      setLandingConfig(prev => ({
                                        ...prev,
                                        segmentation_conditions: conditions.includes(seg.id)
                                          ? conditions.filter(id => id !== seg.id)
                                          : [...conditions, seg.id]
                                      }));
                                    }}
                                  />
                                  <Label 
                                    htmlFor={`cond-${seg.id}`}
                                    className="text-sm font-normal cursor-pointer flex-1"
                                  >
                                    {seg.name}
                                    {seg.description && (
                                      <span className="text-xs text-slate-400 block">{seg.description}</span>
                                    )}
                                  </Label>
                                </div>
                              ))
                          )
                        }
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Info className="h-5 w-5 text-blue-500 mt-0.5" />
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">How it works</p>
                          <p>
                            {(landingConfig.segmentation_conditions || []).length === 0 
                              ? "Currently, all users will see the full segmentation questions regardless of their involvement level."
                              : `Users who select checked involvement levels will proceed to full segmentation. Others will skip directly to self-identification.`
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-amber-500 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        Please select an Involvement Segment Type above to configure segmentation flow conditions.
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Additional Segment Types */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#5AA2AE]" />
                  Additional Segment Types
                </CardTitle>
                <CardDescription>
                  Configure which additional segment types users can self-select (shown as dropdowns after involvement)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Enable Additional Segmentation</Label>
                    <p className="text-xs text-slate-500">Let users select additional segments beyond involvement</p>
                  </div>
                  <Switch
                    checked={landingConfig.allow_self_segmentation}
                    onCheckedChange={(checked) => setLandingConfig(prev => ({ ...prev, allow_self_segmentation: checked }))}
                  />
                </div>

                {landingConfig.allow_self_segmentation && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <Label>Allowed Segment Types</Label>
                      <p className="text-xs text-slate-500 mb-2">
                        Select which segment types users can self-select. Leave empty to allow all (excluding involvement).
                      </p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {(segmentTypes || [])
                          .filter(type => type.id !== landingConfig.involvement_segment_type_id)
                          .map(type => (
                            <div key={type.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50">
                              <Checkbox
                                id={`segtype-${type.id}`}
                                checked={(landingConfig.allowed_segment_type_ids || []).includes(type.id)}
                                onCheckedChange={() => toggleSegmentType(type.id)}
                              />
                              <Label 
                                htmlFor={`segtype-${type.id}`}
                                className="text-sm font-normal cursor-pointer flex-1"
                              >
                                {type.name}
                                {type.description && (
                                  <span className="text-xs text-slate-400 ml-2">- {type.description}</span>
                                )}
                              </Label>
                            </div>
                          ))}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button 
                onClick={saveLandingConfig} 
                disabled={savingConfig}
                className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              >
                <Save className="mr-2 h-4 w-4" />
                {savingConfig ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </TabsContent>

          {/* Self-Identification */}
          <TabsContent value="self_identification" className="space-y-6 mt-6">
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="h-5 w-5 text-[#5AA2AE]" />
                  Self-Identification Settings
                </CardTitle>
                <CardDescription>
                  Allow users to optionally identify themselves for follow-up
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Enable Self-Identification</Label>
                    <p className="text-xs text-slate-500">Ask users if they want to provide their contact details</p>
                  </div>
                  <Switch
                    checked={landingConfig.allow_self_identification}
                    onCheckedChange={(checked) => setLandingConfig(prev => ({ ...prev, allow_self_identification: checked }))}
                  />
                </div>

                {landingConfig.allow_self_identification && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <Label htmlFor="self_id_prompt">Identification Prompt</Label>
                      <Textarea
                        id="self_id_prompt"
                        value={landingConfig.self_id_prompt}
                        onChange={(e) => setLandingConfig(prev => ({ ...prev, self_id_prompt: e.target.value }))}
                        placeholder="Would you like to identify yourself so the project team can follow up?"
                        rows={3}
                      />
                      <p className="text-xs text-slate-500">
                        This message will be shown to users after they complete the survey
                      </p>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Info className="h-5 w-5 text-blue-500 mt-0.5" />
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">Verification Process</p>
                          <p>When a user self-identifies, they will receive a verification email. 
                          Once verified, they will appear in the Stakeholders section under 
                          "Pending Approval" for admin review before being added to the stakeholder list.</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button 
                onClick={saveLandingConfig} 
                disabled={savingConfig}
                className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              >
                <Save className="mr-2 h-4 w-4" />
                {savingConfig ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  };

  // Show message if no drivers and trying to use About Me
  if (drivers.length === 0 && !loading && activeContext === 'about_me') {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Questions
          </h1>
          <p className="text-slate-600 mt-1">Build your survey questions</p>
        </div>

        <div className="flex gap-2 p-1 bg-slate-100 rounded-lg w-fit">
          {questionContexts.map(ctx => {
            const Icon = ctx.icon;
            return (
              <Button
                key={ctx.value}
                variant={activeContext === ctx.value ? 'default' : 'ghost'}
                className={activeContext === ctx.value ? 'bg-[#5AA2AE] hover:bg-[#5AA2AE]/90' : ''}
                onClick={() => setActiveContext(ctx.value)}
                data-testid={`context-${ctx.value}`}
              >
                <Icon className="mr-2 h-4 w-4" />
                {ctx.label}
              </Button>
            );
          })}
        </div>

        <Card className="dashboard-card">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Info className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Create drivers first</h3>
            <p className="text-slate-500 mb-4">
              About Me questions need to be associated with drivers. Please create at least one driver before adding questions.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Questions
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">Build and manage your survey questions</p>
        </div>
        {activeContext !== 'public_landing' && (
          <Button 
            className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 w-full sm:w-auto"
            onClick={openCreateSheet}
            data-testid="create-question-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Question
          </Button>
        )}
      </div>

      {/* Context Toggle */}
      <div className="overflow-x-auto -mx-1 px-1">
        <div className="flex gap-1 sm:gap-2 p-1 bg-slate-100 rounded-lg w-fit min-w-max">
          {questionContexts.map(ctx => {
            const Icon = ctx.icon;
            return (
              <Button
                key={ctx.value}
                variant={activeContext === ctx.value ? 'default' : 'ghost'}
                size="sm"
                className={`${activeContext === ctx.value ? 'bg-[#5AA2AE] hover:bg-[#5AA2AE]/90' : ''} text-xs sm:text-sm`}
                onClick={() => {
                  setActiveContext(ctx.value);
                  setSelectedDriver('all');
                  setSelectedSegment('all');
                }}
                data-testid={`context-${ctx.value}`}
              >
                <Icon className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{ctx.label}</span>
              </Button>
            );
          })}
        </div>
      </div>

      {/* Render based on active context */}
      {activeContext === 'public_landing' ? (
        renderPublicLandingContent()
      ) : (
        <>
          {/* Context Description */}
          {activeContextInfo && (
            <p className="text-sm text-slate-500">{activeContextInfo.description}</p>
          )}

          {/* Filters - Only show for About Me */}
          {activeContext === 'about_me' && (
            <div className="space-y-4">
              {/* Segment Filter - moved above driver toggles */}
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-600">Filter by Segment:</Label>
                <Select value={selectedSegment} onValueChange={setSelectedSegment}>
                  <SelectTrigger className="w-48" data-testid="segment-filter-select">
                    <SelectValue placeholder="All Segments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Segments</SelectItem>
                    {segments.map(segment => (
                      <SelectItem key={segment.id} value={segment.id}>
                        {segment.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Horizontal Driver Toggles - like user's About Me */}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {drivers.map(driver => {
                  // Count questions for this driver from ALL questions (not filtered)
                  const driverQuestions = allAboutMeQuestions.filter(q => q.driver_id === driver.id);
                  const questionCount = driverQuestions.length;
                  const isActive = activeDriverId === driver.id;
                  
                  return (
                    <button
                      key={driver.id}
                      onClick={() => {
                        setActiveDriverId(driver.id);
                        setSelectedDriver(driver.id);
                      }}
                      className={`
                        flex items-center gap-2 px-4 py-3 rounded-xl border transition-all min-w-fit
                        ${isActive 
                          ? 'bg-[#5AA2AE] border-[#5AA2AE] text-white shadow-md' 
                          : 'bg-white border-slate-200 text-slate-700 hover:border-[#5AA2AE]/50'
                        }
                      `}
                      data-testid={`driver-toggle-${driver.id}`}
                    >
                      <span className="font-medium">{driver.name}</span>
                      <Badge 
                        variant={isActive ? "secondary" : "outline"}
                        className={`
                          ${isActive 
                            ? 'bg-white/20 text-white border-white/30' 
                            : 'bg-slate-100 text-slate-600'
                          }
                        `}
                      >
                        {questionCount}
                      </Badge>
                    </button>
                  );
                })}
                {/* All Drivers option */}
                <button
                  onClick={() => {
                    setActiveDriverId(null);
                    setSelectedDriver('all');
                  }}
                  className={`
                    flex items-center gap-2 px-4 py-3 rounded-xl border transition-all min-w-fit
                    ${selectedDriver === 'all' && !activeDriverId
                      ? 'bg-slate-700 border-slate-700 text-white shadow-md' 
                      : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'
                    }
                  `}
                  data-testid="driver-toggle-all"
                >
                  <span className="font-medium">All Drivers</span>
                  <Badge 
                    variant="outline"
                    className={`
                      ${selectedDriver === 'all' && !activeDriverId
                        ? 'bg-white/20 text-white border-white/30' 
                        : 'bg-slate-100 text-slate-600'
                      }
                    `}
                  >
                    {allAboutMeQuestions.length}
                  </Badge>
                </button>
              </div>
            </div>
          )}

          {/* Mapping Tab - Full mapping categories management */}
          {activeContext === 'mapping' && (
            <Card className="dashboard-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <LinkIcon className="h-5 w-5 text-[#5AA2AE]" />
                      Mapping Categories
                    </CardTitle>
                    <CardDescription>
                      Define relationship types that users will select when identifying stakeholders in About Others.
                      These categories help map how stakeholders relate to each other.
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        fetchMappingCategoriesLibrary();
                        setSelectedImportCategories([]);
                        setImportMappingDialogOpen(true);
                      }}
                    >
                      <BookOpen className="h-4 w-4 mr-1" />
                      Import from Library
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setEditingMappingCategory(null);
                        setMappingCategoryForm({ name: '', description: '', icon: '' });
                        setMappingCategoryDialogOpen(true);
                      }}
                      className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Category
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {mappingCategories.length === 0 ? (
                  <div className="text-center py-12">
                    <LinkIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-2">No mapping categories configured</p>
                    <p className="text-sm text-slate-400 mb-4">
                      Import from the global library or create your own categories.
                    </p>
                    <div className="flex justify-center gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          fetchMappingCategoriesLibrary();
                          setSelectedImportCategories([]);
                          setImportMappingDialogOpen(true);
                        }}
                      >
                        <BookOpen className="h-4 w-4 mr-2" />
                        Import from Library
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {mappingCategories.map((cat, index) => (
                      <div
                        key={cat.id}
                        className="flex items-center gap-3 px-4 py-3 bg-white rounded-lg border group hover:shadow-sm transition-shadow"
                      >
                        {/* Reorder buttons */}
                        <div className="flex flex-col gap-0.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveMappingCategory(index, -1)}
                            disabled={index === 0}
                            className="h-5 w-5 p-0 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                          >
                            <ChevronUp className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveMappingCategory(index, 1)}
                            disabled={index === mappingCategories.length - 1}
                            className="h-5 w-5 p-0 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                          >
                            <ChevronDown className="h-3 w-3" />
                          </Button>
                        </div>
                        {/* Category info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900">{cat.name}</p>
                          {cat.description && (
                            <p className="text-sm text-slate-500 truncate">{cat.description}</p>
                          )}
                        </div>
                        {/* From library badge */}
                        {cat.is_from_library && (
                          <Badge variant="outline" className="text-xs">
                            From Library
                          </Badge>
                        )}
                        {/* Edit/Delete buttons */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingMappingCategory(cat);
                              setMappingCategoryForm({
                                name: cat.name,
                                description: cat.description || '',
                                icon: cat.icon || ''
                              });
                              setMappingCategoryDialogOpen(true);
                            }}
                            className="h-7 w-7 p-0"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteMappingCategory(cat.id)}
                            className="h-7 w-7 p-0 text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Questions List - Only show for question contexts */}
          {['about_me', 'about_others'].includes(activeContext) && (
          loading ? (
            <div className="space-y-4">
              {Array(3).fill(0).map((_, i) => (
                <Card key={i} className="dashboard-card">
                  <Skeleton className="h-24" />
                </Card>
              ))}
            </div>
          ) : questions.length === 0 ? (
            <Card className="dashboard-card">
              <CardContent className="py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <FileQuestion className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No questions yet</h3>
                <p className="text-slate-500 mb-4">Create questions for {activeContextInfo?.label}</p>
                <div className="flex items-center justify-center gap-3">
                  <Button 
                    className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                    onClick={openCreateSheet}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add First Question
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {questions.map((question, index) => (
                <QuestionCard 
                  key={question.id}
                  question={question}
                  index={index}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  getTypeLabel={getTypeLabel}
                  getDriverName={getDriverName}
                  getSegmentNames={getSegmentNames}
                  responseStats={responseStats}
                />
              ))}
            </div>
          ))}
        </>
      )}

      {/* Question Editor Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingQuestion ? 'Edit Question' : 'Add New Question'}
            </SheetTitle>
          </SheetHeader>

          {/* Library Button - Only show when creating new question for about_me or about_others */}
          {!editingQuestion && ['about_me', 'about_others'].includes(formData.question_context) && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-dashed">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-[#5AA2AE]" />
                  <div>
                    <p className="text-sm font-medium">Import from Library</p>
                    <p className="text-xs text-slate-500">Use pre-built questions from the global library</p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={openLibrary}
                  className="gap-2"
                  data-testid="open-library-btn"
                >
                  <BookOpen className="h-4 w-4" />
                  Browse Library
                </Button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6 mt-6">
            {/* Question Context - hidden, auto-set from active context */}

            {/* Driver - Only for About Me */}
            {formData.question_context === 'about_me' && (
              <div className="space-y-2">
                <Label>Driver</Label>
                <Select 
                  value={formData.driver_id} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, driver_id: value }))}
                >
                  <SelectTrigger data-testid="question-driver-select">
                    <SelectValue placeholder="Select driver" />
                  </SelectTrigger>
                  <SelectContent>
                    {drivers.map(driver => (
                      <SelectItem key={driver.id} value={driver.id}>
                        {driver.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Heading */}
            <div className="space-y-2">
              <Label htmlFor="heading">Heading *</Label>
              <Input
                id="heading"
                placeholder="e.g., Communication"
                value={formData.heading}
                onChange={(e) => setFormData(prev => ({ ...prev, heading: e.target.value }))}
                required
                data-testid="question-heading-input"
              />
              <p className="text-xs text-slate-500">Required. Used to group questions on dashboards.</p>
            </div>

            {/* Question Text */}
            <div className="space-y-2">
              <Label htmlFor="text">Question Text</Label>
              <Textarea
                id="text"
                placeholder="e.g., How satisfied are you with the communication from leadership?"
                value={formData.text}
                onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
                rows={3}
                required
                data-testid="question-text-input"
              />
            </div>

            {/* Question Type */}
            <div className="space-y-2">
              <Label>Question Type</Label>
              <Select 
                value={formData.question_type} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, question_type: value }))}
              >
                <SelectTrigger data-testid="question-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {questionTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Options (for multiple choice, dropdown, checkbox) */}
            {['multiple_choice', 'dropdown', 'checkbox'].includes(formData.question_type) && (
              <div className="space-y-2">
                <Label>Options</Label>
                <div className="space-y-2">
                  {formData.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        placeholder={`Option ${index + 1}`}
                        value={option}
                        onChange={(e) => updateOption(index, e.target.value)}
                        data-testid={`question-option-${index}`}
                      />
                      {formData.options.length > 1 && (
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon"
                          onClick={() => removeOption(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addOption}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Option
                  </Button>
                </div>
              </div>
            )}

            {/* Slider Config */}
            {formData.question_type === 'slider' && (() => {
              const hasCustomColors = formData.slider_config.left_color || formData.slider_config.middle_color || formData.slider_config.right_color;
              
              const enableCustomColors = () => {
                setFormData(prev => ({
                  ...prev,
                  slider_config: {
                    ...prev.slider_config,
                    left_color: customGradientColors.left_color,
                    middle_color: customGradientColors.middle_color,
                    right_color: customGradientColors.right_color
                  }
                }));
              };
              
              const resetToDefaults = () => {
                setFormData(prev => ({
                  ...prev,
                  slider_config: {
                    ...prev.slider_config,
                    left_color: null,
                    middle_color: null,
                    right_color: null
                  }
                }));
              };
              
              return (
                <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                  <Label className="text-sm font-medium">Slider Labels</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Left</Label>
                      <Input
                        placeholder="Low"
                        value={formData.slider_config.left_label}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          slider_config: { ...prev.slider_config, left_label: e.target.value }
                        }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Middle</Label>
                      <Input
                        placeholder="Medium"
                        value={formData.slider_config.middle_label}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          slider_config: { ...prev.slider_config, middle_label: e.target.value }
                        }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Right</Label>
                      <Input
                        placeholder="High"
                        value={formData.slider_config.right_label}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          slider_config: { ...prev.slider_config, right_label: e.target.value }
                        }))}
                      />
                    </div>
                  </div>
                  
                  {/* Custom Colors Toggle */}
                  {!hasCustomColors ? (
                    <button
                      type="button"
                      onClick={enableCustomColors}
                      className="text-sm text-[#5AA2AE] hover:text-[#5AA2AE]/80 hover:underline"
                    >
                      + Add Custom Colors
                    </button>
                  ) : (
                    <>
                      <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-200">
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-500">Left Color</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={formData.slider_config.left_color || '#ef4444'}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                slider_config: { ...prev.slider_config, left_color: e.target.value }
                              }))}
                              className="w-8 h-8 rounded cursor-pointer border border-slate-200"
                            />
                            <Input
                              placeholder="#ef4444"
                              value={formData.slider_config.left_color || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                slider_config: { ...prev.slider_config, left_color: e.target.value }
                              }))}
                              className="flex-1 h-8 text-xs font-mono"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-500">Middle Color</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={formData.slider_config.middle_color || '#eab308'}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                slider_config: { ...prev.slider_config, middle_color: e.target.value }
                              }))}
                              className="w-8 h-8 rounded cursor-pointer border border-slate-200"
                            />
                            <Input
                              placeholder="#eab308"
                              value={formData.slider_config.middle_color || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                slider_config: { ...prev.slider_config, middle_color: e.target.value }
                              }))}
                              className="flex-1 h-8 text-xs font-mono"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-500">Right Color</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={formData.slider_config.right_color || '#22c55e'}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                slider_config: { ...prev.slider_config, right_color: e.target.value }
                              }))}
                              className="w-8 h-8 rounded cursor-pointer border border-slate-200"
                            />
                            <Input
                              placeholder="#22c55e"
                              value={formData.slider_config.right_color || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                slider_config: { ...prev.slider_config, right_color: e.target.value }
                              }))}
                              className="flex-1 h-8 text-xs font-mono"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-slate-400">Colors are used for gradient visualization in reports.</p>
                        <button
                          type="button"
                          onClick={resetToDefaults}
                          className="text-xs text-[#5AA2AE] hover:text-[#5AA2AE]/80 hover:underline"
                        >
                          Use Defaults
                        </button>
                      </div>
                    </>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Min Value</Label>
                      <Input
                        type="number"
                        value={formData.slider_config.min_value}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          slider_config: { ...prev.slider_config, min_value: parseInt(e.target.value) }
                        }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Max Value</Label>
                      <Input
                        type="number"
                        value={formData.slider_config.max_value}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          slider_config: { ...prev.slider_config, max_value: parseInt(e.target.value) }
                        }))}
                      />
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Segments - Show for About Me and Public Landing */}
            {(formData.question_context === 'about_me' || formData.question_context === 'public_landing') && segments.length > 0 && (
              <div className="space-y-2">
                <Label>Target Segments (Optional)</Label>
                <p className="text-xs text-slate-500 mb-2">
                  {formData.question_context === 'public_landing' 
                    ? 'Show this question only to users who self-identify with selected segments. Leave empty to show to all.'
                    : 'Leave empty to show to all stakeholders'
                  }
                </p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {segments.map(segment => (
                    <div key={segment.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`segment-${segment.id}`}
                        checked={formData.segment_ids.includes(segment.id)}
                        onCheckedChange={() => toggleSegment(segment.id)}
                      />
                      <Label 
                        htmlFor={`segment-${segment.id}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {segment.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Required */}
            <div className="flex items-center justify-between">
              <Label htmlFor="required">Required question</Label>
              <Switch
                id="required"
                checked={formData.required}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, required: checked }))}
              />
            </div>

            {/* Allow Skip */}
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="allow_skip">Allow Skip?</Label>
                <p className="text-xs text-slate-500">Show a "Skip" option with reason selection</p>
              </div>
              <Switch
                id="allow_skip"
                checked={formData.allow_skip}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, allow_skip: checked }))}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setSheetOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                disabled={submitting || !formData.heading?.trim() || !formData.text?.trim()}
                data-testid="question-submit-btn"
              >
                {submitting ? 'Saving...' : (editingQuestion ? 'Update' : 'Create')}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Add Question Wizard */}
      <AddQuestionWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={fetchQuestions}
        projectId={projectId}
        drivers={drivers}
        segments={segments}
        activeContext={activeContext}
        token={token}
      />

      {/* Question Library Dialog */}
      <Dialog open={libraryOpen} onOpenChange={setLibraryOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-[#5AA2AE]" />
              Question Library - {formData.question_context === 'about_me' ? 'About Me' : 'About Others'}
            </DialogTitle>
            <DialogDescription>
              Select a question to import into your project. The question will be added with your current driver selection.
            </DialogDescription>
          </DialogHeader>
          
          {/* Filter Toggle */}
          {formData.question_context === 'about_me' && formData.driver_id && (
            <div className="flex items-center gap-2 py-2">
              <Checkbox
                id="filter-drivers"
                checked={filterByDrivers}
                onCheckedChange={setFilterByDrivers}
              />
              <Label htmlFor="filter-drivers" className="text-sm font-normal cursor-pointer">
                Show only questions that match "{drivers.find(d => d.id === formData.driver_id)?.name || 'selected driver'}"
              </Label>
            </div>
          )}
          
          <div className="flex-1 overflow-y-auto space-y-2 py-4">
            {libraryLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : libraryQuestions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                <FileQuestion className="h-12 w-12 mb-4 text-slate-300" />
                <p className="font-medium">No questions in the library</p>
                <p className="text-sm mt-1">Contact your administrator to add questions to the library</p>
              </div>
            ) : (
              libraryQuestions.map((question) => (
                <div
                  key={question.id}
                  className="flex items-start justify-between p-4 bg-slate-50 rounded-lg border hover:border-[#5AA2AE] hover:bg-slate-100 cursor-pointer transition-colors"
                  onClick={() => importFromLibrary(question)}
                  data-testid={`library-question-${question.id}`}
                >
                  <div className="flex-1">
                    {question.heading && (
                      <p className="text-xs font-semibold text-[#5AA2AE] uppercase tracking-wide mb-1">
                        {question.heading}
                      </p>
                    )}
                    <p className="text-sm font-medium text-slate-900">{question.text}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="secondary" className="text-xs">
                        {questionTypes.find(t => t.value === question.question_type)?.label || question.question_type}
                      </Badge>
                      {question.required && (
                        <Badge variant="outline" className="text-xs border-amber-300 text-amber-600">
                          Required
                        </Badge>
                      )}
                      {question.options?.length > 0 && (
                        <span className="text-xs text-slate-400">
                          {question.options.length} options
                        </span>
                      )}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="ml-2">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Mapping Category Edit/Create Dialog */}
      <Dialog open={mappingCategoryDialogOpen} onOpenChange={setMappingCategoryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingMappingCategory ? 'Edit Mapping Category' : 'Add Mapping Category'}
            </DialogTitle>
            <DialogDescription>
              Define a relationship type for stakeholder mapping
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="mc-name">Name *</Label>
              <Input
                id="mc-name"
                placeholder="e.g., My Leader, My Peer"
                value={mappingCategoryForm.name}
                onChange={(e) => setMappingCategoryForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mc-description">Description</Label>
              <Textarea
                id="mc-description"
                placeholder="Describe this relationship type..."
                value={mappingCategoryForm.description}
                onChange={(e) => setMappingCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setMappingCategoryDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveMappingCategory}
              disabled={!mappingCategoryForm.name.trim()}
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
            >
              {editingMappingCategory ? 'Update' : 'Add Category'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Mapping Categories from Library Dialog */}
      <Dialog open={importMappingDialogOpen} onOpenChange={setImportMappingDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-[#5AA2AE]" />
              Import Mapping Categories
            </DialogTitle>
            <DialogDescription>
              Select categories from the global library to add to this project
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto py-4">
            {mappingCategoriesLibrary.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">
                No mapping categories in the library. Contact your administrator.
              </p>
            ) : (
              mappingCategoriesLibrary.map(cat => {
                const alreadyImported = mappingCategories.some(c => c.library_category_id === cat.id || c.name === cat.name);
                const isSelected = selectedImportCategories.includes(cat.id);
                return (
                  <div
                    key={cat.id}
                    onClick={() => {
                      if (alreadyImported) return;
                      setSelectedImportCategories(prev => 
                        isSelected 
                          ? prev.filter(id => id !== cat.id)
                          : [...prev, cat.id]
                      );
                    }}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      alreadyImported 
                        ? 'bg-slate-50 opacity-50 cursor-not-allowed' 
                        : isSelected
                          ? 'border-[#5AA2AE] bg-[#5AA2AE]/10 cursor-pointer'
                          : 'hover:border-[#5AA2AE] hover:bg-slate-50 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={isSelected}
                        disabled={alreadyImported}
                        className={isSelected ? 'border-[#5AA2AE] data-[state=checked]:bg-[#5AA2AE]' : ''}
                      />
                      <div>
                        <p className="font-medium text-sm">{cat.name}</p>
                        {cat.description && <p className="text-xs text-slate-500">{cat.description}</p>}
                      </div>
                    </div>
                    {alreadyImported && (
                      <Badge variant="secondary" className="text-xs">Already Added</Badge>
                    )}
                  </div>
                );
              })
            )}
          </div>
          <div className="flex justify-between items-center">
            <div className="text-sm text-slate-500">
              {selectedImportCategories.length > 0 && (
                <span>{selectedImportCategories.length} selected</span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => {
                setImportMappingDialogOpen(false);
                setSelectedImportCategories([]);
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleImportMappingCategories}
                disabled={selectedImportCategories.length === 0}
                className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              >
                Import {selectedImportCategories.length > 0 ? `(${selectedImportCategories.length})` : ''}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Question?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Are you sure you want to delete this question?
              </p>
              {deleteTarget && (
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <p className="font-medium text-slate-900">{deleteTarget.text}</p>
                </div>
              )}
              <p className="text-amber-600 font-medium">
                ⚠️ Warning: Any existing user responses to this question will also be permanently deleted.
              </p>
              <div className="pt-2 border-t">
                <p className="text-sm text-slate-600 mb-2">
                  Alternatively, you can <strong>hide</strong> the question to remove it from user view while preserving all historical responses.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button 
              variant="outline"
              onClick={() => {
                setDeleteAction('hide');
                confirmDelete();
              }}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              Hide Question (Keep Responses)
            </Button>
            <AlertDialogAction 
              onClick={() => {
                setDeleteAction('delete');
                confirmDelete();
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Warning Dialog */}
      <AlertDialog open={editWarningOpen} onOpenChange={setEditWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <Pencil className="h-5 w-5" />
              Edit Question?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                You are about to edit this question:
              </p>
              {pendingEdit && (
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <p className="font-medium text-slate-900">{pendingEdit.text}</p>
                </div>
              )}
              <p className="text-amber-600 font-medium">
                ⚠️ Warning: Editing a question may make existing user responses invalid or confusing if the question meaning changes significantly.
              </p>
              <p className="text-sm text-slate-600">
                Consider creating a new question instead if you need to ask something substantially different.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingEdit(null)}>Cancel</AlertDialogCancel>
            <Button 
              variant="outline"
              onClick={() => {
                setEditWarningOpen(false);
                handleDelete(pendingEdit);
              }}
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              Delete & Create New
            </Button>
            <AlertDialogAction 
              onClick={confirmEdit}
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
            >
              Edit Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}

// Question Card Component with Response Stats
function QuestionCard({ question, index, onEdit, onDelete, getTypeLabel, getDriverName, getSegmentNames, responseStats }) {
  const stats = responseStats?.[question.id];
  const [showSkipReasons, setShowSkipReasons] = useState(false);
  
  return (
    <Card className="dashboard-card group" data-testid={`question-card-${question.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="flex items-center gap-2 text-slate-400 pt-1">
            <GripVertical className="w-5 h-5 cursor-grab" />
            <span className="text-sm font-medium w-6">{index + 1}</span>
          </div>
          <div className="flex-1 min-w-0">
            {question.heading && (
              <p className="text-xs font-semibold text-[#5AA2AE] uppercase tracking-wide mb-1">
                {question.heading}
              </p>
            )}
            <p className="text-slate-900 font-medium mb-2">{question.text}</p>
            
            {/* Response Rate Visualization */}
            {stats && stats.total_respondents > 0 && (
              <div className="mb-3 space-y-2">
                {/* Progress bar showing response breakdown - Order: Not Answered, Answered, Skipped */}
                <div className="flex h-2 rounded-full overflow-hidden bg-slate-100">
                  {stats.not_answered_percent > 0 && (
                    <div 
                      className="bg-slate-300 transition-all" 
                      style={{ width: `${stats.not_answered_percent}%` }}
                      title={`Not Answered: ${stats.not_answered_percent}%`}
                    />
                  )}
                  {stats.answered_percent > 0 && (
                    <div 
                      className="bg-emerald-500 transition-all" 
                      style={{ width: `${stats.answered_percent}%` }}
                      title={`Answered: ${stats.answered_percent}%`}
                    />
                  )}
                  {stats.skipped_percent > 0 && (
                    <div 
                      className="bg-amber-400 transition-all" 
                      style={{ width: `${stats.skipped_percent}%` }}
                      title={`Skipped: ${stats.skipped_percent}%`}
                    />
                  )}
                </div>
                
                {/* Response rate labels - Order: Not Answered, Answered, Skipped */}
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                    <span className="text-slate-600">
                      Not Answered: <span className="font-medium">{stats.not_answered}</span> ({stats.not_answered_percent}%)
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    <span className="text-slate-600">
                      Answered: <span className="font-medium">{stats.answered}</span> ({stats.answered_percent}%)
                    </span>
                  </div>
                  
                  {/* Skipped with hover for reasons */}
                  <div 
                    className="flex items-center gap-1.5 relative"
                    onMouseEnter={() => setShowSkipReasons(true)}
                    onMouseLeave={() => setShowSkipReasons(false)}
                  >
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                    <span className="text-slate-600 cursor-help border-b border-dashed border-slate-400">
                      Skipped: <span className="font-medium">{stats.skipped}</span> ({stats.skipped_percent}%)
                    </span>
                    
                    {/* Skip reasons tooltip */}
                    {showSkipReasons && stats.skip_reasons && stats.skip_reasons.length > 0 && (
                      <div className="absolute bottom-full left-0 mb-2 z-50 bg-slate-800 text-white rounded-lg shadow-lg p-3 min-w-[200px]">
                        <p className="text-xs font-medium mb-2 text-slate-300">Skip Reasons:</p>
                        <div className="space-y-1.5">
                          {stats.skip_reasons.map((reason, idx) => (
                            <div key={idx} className="flex justify-between text-xs">
                              <span>{reason.label}</span>
                              <span className="font-medium ml-4">{reason.percent}%</span>
                            </div>
                          ))}
                        </div>
                        <div className="absolute bottom-0 left-4 transform translate-y-full">
                          <div className="border-8 border-transparent border-t-slate-800" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {getTypeLabel(question.question_type)}
              </Badge>
              {question.driver_id && (
                <Badge variant="outline" className="text-xs">
                  {getDriverName(question.driver_id)}
                </Badge>
              )}
              <span className="text-xs text-slate-400">
                {getSegmentNames(question.segment_ids)}
              </span>
              {question.allow_skip && (
                <Badge variant="outline" className="text-xs border-amber-300 text-amber-600">
                  Skippable
                </Badge>
              )}
            </div>
            
            {/* Question type features - slider labels or options */}
            {question.question_type === 'slider' && question.slider_config && (
              <div className="mt-1.5 text-xs text-slate-400 italic">
                <span className="text-slate-500">Scale: </span>
                {question.slider_config.left_label || 'Low'}
                {question.slider_config.middle_label && ` → ${question.slider_config.middle_label}`}
                {' → '}
                {question.slider_config.right_label || 'High'}
              </div>
            )}
            
            {(question.question_type === 'multiple_choice' || 
              question.question_type === 'dropdown' || 
              question.question_type === 'checkbox') && 
              question.options && question.options.length > 0 && (
              <div className="mt-1.5 text-xs text-slate-400 italic truncate max-w-full">
                <span className="text-slate-500">Options: </span>
                {question.options.map(opt => typeof opt === 'string' ? opt : opt.label || opt.value).join(', ')}
              </div>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(question)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-red-600"
                onClick={() => onDelete(question)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
}

// Public Link Card Component with QR Code
function PublicLinkCard({ projectId }) {
  const [copied, setCopied] = useState(false);
  const [qrCopied, setQrCopied] = useState(false);
  const qrRef = useRef(null);
  
  // Generate the public landing page URL
  const baseUrl = window.location.origin;
  const publicUrl = `${baseUrl}/public/${projectId}`;
  
  const copyLink = async () => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(publicUrl);
        setCopied(true);
        toast.success('Link copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
        return;
      }
    } catch (err) {
      console.log('Clipboard API failed, trying fallback:', err);
    }
    
    // Fallback: select the input text for user to copy
    try {
      const input = document.querySelector('[data-testid="public-link-input"]');
      if (input) {
        input.select();
        input.setSelectionRange(0, 99999); // For mobile
        const success = document.execCommand('copy');
        if (success) {
          setCopied(true);
          toast.success('Link copied to clipboard');
          setTimeout(() => setCopied(false), 2000);
          return;
        }
      }
    } catch (err) {
      console.log('execCommand fallback failed:', err);
    }
    
    // Final fallback: just select the text
    try {
      const input = document.querySelector('[data-testid="public-link-input"]');
      if (input) {
        input.select();
        toast.info('Link selected. Press Ctrl+C (or Cmd+C) to copy.');
        return;
      }
    } catch (err) {
      console.error('All copy methods failed:', err);
    }
    
    toast.error('Unable to copy. Please select the link and copy manually.');
  };
  
  const downloadQRCode = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) {
      toast.error('QR code not found');
      return;
    }
    
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const svgData = new XMLSerializer().serializeToString(svg);
      const img = new Image();
      
      img.onload = () => {
        canvas.width = 280;
        canvas.height = 280;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        const link = document.createElement('a');
        link.download = `pulse-qr-${projectId.substring(0, 8)}.png`;
        link.href = canvas.toDataURL('image/png');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('QR code downloaded');
      };
      
      img.onerror = () => {
        toast.error('Failed to generate QR code image');
      };
      
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Failed to download QR code');
    }
  };
  
  const copyQRCode = async () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) {
      toast.error('QR code not found');
      return;
    }
    
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const svgData = new XMLSerializer().serializeToString(svg);
      const img = new Image();
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
      });
      
      canvas.width = 280;
      canvas.height = 280;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // Check if ClipboardItem is available
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ]);
        setQrCopied(true);
        toast.success('QR code copied to clipboard');
        setTimeout(() => setQrCopied(false), 2000);
      } else {
        // Clipboard API not supported, download instead
        toast.info('Copying images not supported in this browser. Downloading instead...');
        downloadQRCode();
      }
    } catch (err) {
      console.error('Copy QR failed:', err);
      // Fallback to download
      toast.info('Could not copy to clipboard. Downloading instead...');
      downloadQRCode();
    }
  };

  return (
    <Card className="dashboard-card border-[#5AA2AE]/20 bg-gradient-to-br from-[#5AA2AE]/5 to-transparent">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <LinkIcon className="h-5 w-5 text-[#5AA2AE]" />
          Public Landing Page Link
        </CardTitle>
        <CardDescription>
          Share this link with stakeholders to collect their feedback
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Link Section */}
          <div className="flex-1 space-y-4">
            <div className="space-y-2">
              <Label className="text-sm text-slate-600">Shareable Link</Label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Input
                    readOnly
                    value={publicUrl}
                    className="pr-10 bg-white font-mono text-sm cursor-text select-all"
                    data-testid="public-link-input"
                    onClick={(e) => e.target.select()}
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={copyLink}
                  className="shrink-0"
                  data-testid="copy-link-btn"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  <span className="ml-2">{copied ? 'Copied!' : 'Copy'}</span>
                </Button>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                <strong>Tip:</strong> Share this link via email, social media, or embed it in your communications 
                to collect feedback from stakeholders.
              </p>
            </div>
          </div>
          
          {/* QR Code Section */}
          <div className="flex flex-col items-center gap-3 p-4 bg-white rounded-lg border">
            <div ref={qrRef} className="p-3 bg-white rounded-lg">
              <QRCodeSVG 
                value={publicUrl} 
                size={140}
                level="M"
                includeMargin={false}
                bgColor="#ffffff"
                fgColor="#1e293b"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={copyQRCode}
                className="text-xs"
                data-testid="copy-qr-btn"
              >
                {qrCopied ? (
                  <Check className="h-3 w-3 mr-1 text-green-500" />
                ) : (
                  <Copy className="h-3 w-3 mr-1" />
                )}
                {qrCopied ? 'Copied!' : 'Copy'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadQRCode}
                className="text-xs"
                data-testid="download-qr-btn"
              >
                <Download className="h-3 w-3 mr-1" />
                Download
              </Button>
            </div>
            <p className="text-xs text-slate-500 text-center">
              Scan to open the landing page
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

